/**
 * User preferences for the whole web app.
 *
 * One typed store, persisted to localStorage immediately and mirrored into
 * `profiles.preferences` when a session exists. Local-first matters: theme and
 * reading preferences must apply on the very first paint, before Supabase has
 * answered, or the page visibly flashes the wrong colours.
 *
 * Consumers subscribe through `useSettings()`, so a change on the Settings page
 * propagates to the reader, the feed and the nav bar at once.
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type AccentColor = 'indigo' | 'violet' | 'emerald' | 'rose' | 'amber';
export type Density = 'comfortable' | 'compact';
export type ReaderFont = 'serif' | 'sans' | 'mono';
export type ReaderWidth = 'narrow' | 'regular' | 'wide';

export interface UserSettings {
  /* Appearance */
  theme: ThemeMode;
  accent: AccentColor;
  density: Density;
  reduceMotion: boolean;

  /* Reading */
  readerFont: ReaderFont;
  readerSize: number;
  readerWidth: ReaderWidth;
  readerLineHeight: number;
  immersiveMode: boolean;

  /* Notifications */
  notifyComments: boolean;
  notifyFollows: boolean;
  notifyLikes: boolean;
  notifyCompetitions: boolean;
  pushEnabled: boolean;

  /* Privacy */
  showReadingActivity: boolean;
  showOnlineStatus: boolean;
  discoverableInSearch: boolean;
  personalisedFeed: boolean;

  /* Identity */
  nameDisplayPreference: 'display_name' | 'full_name';

  /* Content safety */
  hideMatureContent: boolean;
  reduceDataUsage: boolean;
}

export const DEFAULT_SETTINGS: UserSettings = {
  theme: 'system',
  accent: 'indigo',
  density: 'comfortable',
  reduceMotion: false,

  readerFont: 'serif',
  readerSize: 18,
  readerWidth: 'regular',
  readerLineHeight: 1.8,
  immersiveMode: false,

  notifyComments: true,
  notifyFollows: true,
  notifyLikes: false,
  notifyCompetitions: true,
  pushEnabled: false,

  showReadingActivity: true,
  showOnlineStatus: true,
  discoverableInSearch: true,
  personalisedFeed: true,

  nameDisplayPreference: 'display_name',

  hideMatureContent: false,
  reduceDataUsage: false,
};

const STORAGE_KEY = 'literally:settings:v1';

/** Accent values kept in one place so the CSS and the picker cannot disagree. */
export const ACCENTS: Record<
  AccentColor,
  { label: string; hex: string; classes: { bg: string; text: string; ring: string; border: string } }
> = {
  indigo: {
    label: 'Indigo',
    hex: '#5F4BFF',
    classes: { bg: 'bg-indigo-600', text: 'text-indigo-600', ring: 'ring-indigo-500', border: 'border-indigo-500' },
  },
  violet: {
    label: 'Violet',
    hex: '#8B5CF6',
    classes: { bg: 'bg-violet-600', text: 'text-violet-600', ring: 'ring-violet-500', border: 'border-violet-500' },
  },
  emerald: {
    label: 'Emerald',
    hex: '#10B981',
    classes: { bg: 'bg-emerald-600', text: 'text-emerald-600', ring: 'ring-emerald-500', border: 'border-emerald-500' },
  },
  rose: {
    label: 'Rose',
    hex: '#F43F5E',
    classes: { bg: 'bg-rose-600', text: 'text-rose-600', ring: 'ring-rose-500', border: 'border-rose-500' },
  },
  amber: {
    label: 'Amber',
    hex: '#F59E0B',
    classes: { bg: 'bg-amber-500', text: 'text-amber-600', ring: 'ring-amber-500', border: 'border-amber-500' },
  },
};
/** Keeps the numeric preferences inside sane, readable bounds. */
function clampNumber(key: keyof UserSettings, value: number, fallback: number): number {
  switch (key) {
    case 'readerSize':
      return Math.min(32, Math.max(13, value));
    case 'readerLineHeight':
      return Math.min(2.6, Math.max(1.2, Math.round(value * 10) / 10));
    default:
      return value || fallback;
  }
}

/**
 * Merges stored values over the defaults.
 *
 * Validation is per-key rather than a blanket type check: one bad value written
 * by an older build must not wipe every other preference, and unknown keys from
 * a newer build are simply ignored.
 */
function coerce(raw: unknown): UserSettings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_SETTINGS };
  const source = raw as Partial<Record<keyof UserSettings, unknown>>;
  const out: UserSettings = { ...DEFAULT_SETTINGS };

  (Object.keys(DEFAULT_SETTINGS) as Array<keyof UserSettings>).forEach((key) => {
    const value = source[key];
    if (value === undefined || value === null) return;
    const fallback = DEFAULT_SETTINGS[key];
    if (typeof fallback === 'boolean') {
      if (typeof value === 'boolean') (out[key] as boolean) = value;
    } else if (typeof fallback === 'number') {
      const n = Number(value);
      if (Number.isFinite(n)) (out[key] as number) = clampNumber(key, n, fallback);
    } else if (typeof value === typeof fallback) {
      (out[key] as unknown) = value;
    }
  });

  return out;
}

function readStorage(): UserSettings {
  if (typeof window === 'undefined') return { ...DEFAULT_SETTINGS };
  try {
    return coerce(JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}'));
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

let current: UserSettings = readStorage();
const listeners = new Set<() => void>();

function persist(next: UserSettings) {
  current = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private mode or a full quota - keep the in-memory value so the UI still works.
  }
  listeners.forEach((fn) => fn());
  window.dispatchEvent(new CustomEvent('literally:settings', { detail: next }));
}

/** Subscribes a component to the store. */
export function useSettings() {
  const settings = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => current,
    () => current
  );

  const update = useCallback(<K extends keyof UserSettings>(key: K, value: UserSettings[K]) => {
    persist({ ...current, [key]: value });
  }, []);

  const reset = useCallback(() => persist({ ...DEFAULT_SETTINGS }), []);
  const importSettings = useCallback((raw: unknown) => persist(coerce(raw)), []);

  return useMemo(
    () => ({ settings, update, reset, importSettings }),
    [settings, update, reset, importSettings]
  );
}

/** Resolves `system` against the OS preference. */
export function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode !== 'system') return mode;
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Applies the layout-affecting settings to `<html>`.
 *
 * Driven from `App.tsx` so a single place owns document-level styling, rather
 * than every screen poking at the DOM.
 */
export function applySettingsToDocument(settings: UserSettings) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const dark = resolveTheme(settings.theme) === 'dark';

  root.classList.toggle('dark', dark);
  root.style.setProperty('--reader-size', `${settings.readerSize}px`);
  root.style.setProperty('--reader-leading', String(settings.readerLineHeight));
  root.style.setProperty('--accent', ACCENTS[settings.accent].hex);
  root.style.setProperty(
    '--reader-width',
    settings.readerWidth === 'narrow' ? '42rem' : settings.readerWidth === 'wide' ? '62rem' : '52rem'
  );

  // Honour both the user's preference and the OS "reduce motion" setting.
  const calm = settings.reduceMotion || Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  root.classList.toggle('literally-calm', calm);
}

/** Non-React read, for service workers and one-off utilities. */
export function getSettings(): UserSettings {
  return current;
}

/** Exports the settings as a JSON file the reader can keep or re-import. */
export function downloadSettingsExport(settings: UserSettings) {
  const blob = new Blob([JSON.stringify({ app: 'literally', exportedAt: new Date().toISOString(), settings }, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `literally-settings-${new Date().toISOString().slice(0, 10)}.json`;
  link.click();
  URL.revokeObjectURL(url);
}